// src/components/Pricing.tsx
import { Box, Card, CardContent, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

const plans = [
  {
    title: '4 hour',
    price: '$20',
    features: ['24/7 ADT Monitoring', 'Smart Lock Access', 'Real-time Monitoring'],
  },
  {
    title: '8 hour',
    price: '$35',
    features: ['AI theft prevention', 'All basic features', 'AI security system'],
    popular: true,
  },
  {
    title: '24 hour',
    price: '$50',
    features: ['Smart Lock Access', 'All Premium Features', '24/7 Support'],
  },
];

const cardSx = (popular: boolean | undefined, theme: any) => ({
  bgcolor: popular ? theme.palette.primary.main : theme.palette.background.paper,
  color: '#fff',
  border: popular ? `2px solid ${theme.palette.primary.main}` : '1px solid #333',
  boxShadow: popular ? 6 : 2,
  borderRadius: 3,
  p: { xs: 2, sm: 3 },
  minHeight: { xs: 240, sm: 280 },
  textAlign: 'center',
  position: 'relative',
  height: '100%',
});

const popularLabelSx = (theme: any) => ({
  position: 'absolute',
  top: { xs: 12, sm: 16 },
  left: '50%',
  transform: 'translateX(-50%)',
  bgcolor: '#fff',
  color: theme.palette.primary.main,
  px: { xs: 1.5, sm: 2 },
  py: 0.5,
  borderRadius: 2,
  fontWeight: 700,
  fontSize: { xs: 12, sm: 14 },
});

export default function Pricing() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Box sx={{ 
      py: { xs: 4, sm: 6 },
      px: { xs: 2, sm: 3, md: 4 }
    }}>
      <Typography 
        variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h2'} 
        color="#fff" 
        align="center" 
        fontWeight={700} 
        sx={{ 
          mb: { xs: 3, sm: 4 },
          fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' },
          lineHeight: 1.2
        }}
      >
        Transparent Pricing. No Hidden Fees. Total Peace Of Mind.
      </Typography>
      <Grid 
        container 
        spacing={{ xs: 2, sm: 3, md: 4 }} 
        justifyContent="center" 
        sx={{ 
          maxWidth: 1200, 
          mx: 'auto',
          px: { xs: 1, sm: 2 }
        }}
      >
        {plans.map((plan) => (
          <Grid size={{xs:12, sm:6, md:4}} key={plan.title}>
            <Card
              variant="outlined"
              sx={cardSx(plan.popular, theme)}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                {plan.popular && (
                  <Typography sx={popularLabelSx(theme)}>
                    Popular
                  </Typography>
                )}
                <Typography 
                  variant={isMobile ? 'h6' : 'h5'} 
                  sx={{ 
                    mt: plan.popular ? { xs: 3, sm: 4 } : 0,
                    fontSize: { xs: '1.125rem', sm: '1.25rem' }
                  }}
                >
                  {plan.title}
                </Typography>
                <Typography 
                  variant={isMobile ? 'h4' : 'h3'} 
                  fontWeight={700} 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                  }}
                >
                  {plan.price}
                </Typography>
                {plan.features.map((feature) => (
                  <Stack 
                    direction="row" 
                    spacing={{ xs: 1, sm: 2 }} 
                    key={feature}
                    sx={{ 
                      mb: { xs: 0.5, sm: 1 },
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  >
                    <img 
                      src='icons/tick-icon.svg' 
                      width={isMobile ? 24 : 30} 
                      alt="Included feature" 
                    />
                    <Typography 
                      sx={{ 
                        color: plan.popular ? '#fff' : '#e0e0e0', 
                        fontSize: { xs: 14, sm: 16 }, 
                        textAlign: 'left',
                        flex: 1
                      }}
                    >
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
